import React, { useState, useEffect, useRef } from 'react';
import { Search, Users, Plus, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Document Party Picker
 * Reusable party selection component
 */
const DocumentPartyPicker = ({
  partyType = 'customer', // 'customer' or 'supplier'
  selectedParty,
  onSelectParty,
  onSearch,
  searchResults = [],
  loading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  useEffect(() => {
    if (searchTerm.length > 2) {
      const timer = setTimeout(() => {
        onSearchRef.current(searchTerm);
        setShowDropdown(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setShowDropdown(false);
    }
  }, [searchTerm]);

  const handleSelect = (party) => {
    onSelectParty(party);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const partyLabel = partyType === 'customer' ? 'Customer' : 'Supplier';

  return (
    <Card className="p-4 lg:p-6 border-2 shadow-lg" style={{
      borderColor: '#dbeafe',
      background: 'linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)'
    }}>
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2 mb-3">
            <div className="p-2 rounded-lg" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)' }}>
              <Users className="w-4 h-4 text-blue-600" />
            </div>
            {partyLabel} Details
          </h3>

          {selectedParty ? (
            <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-bold text-gray-900 text-lg">{selectedParty.name}</p>
                  {selectedParty.mobile && (
                    <p className="text-sm text-gray-600 mt-1">Phone: {selectedParty.mobile}</p>
                  )}
                  {selectedParty.email && (
                    <p className="text-sm text-gray-600">Email: {selectedParty.email}</p>
                  )}
                  {selectedParty.gstin && (
                    <p className="text-xs text-gray-500 mt-2">GSTIN: {selectedParty.gstin}</p>
                  )}
                </div>
                <button
                  onClick={() => onSelectParty(null)}
                  className="p-1 hover:bg-red-100 rounded transition-colors"
                  title="Change party"
                >
                  <X className="w-4 h-4 text-red-600" />
                </button>
              </div>
            </div>
          ) : (
            <div className="relative">
              <Label className="text-xs text-gray-600 mb-2 block">Search {partyLabel}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                  placeholder={`Search by name, mobile, email...`}
                  className="pl-10 border-2 focus:border-blue-500"
                />
              </div>

              {showDropdown && searchResults.length > 0 && (
                <div className="absolute z-30 w-full mt-2 bg-white border-2 border-blue-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                  {searchResults.map(party => (
                    <div
                      key={party.id}
                      onClick={() => handleSelect(party)}
                      className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 transition-colors"
                    >
                      <p className="font-semibold text-gray-900">{party.name}</p>
                      {party.mobile && (
                        <p className="text-xs text-gray-600 mt-1">Phone: {party.mobile}</p>
                      )}
                      {party.company_name && (
                        <p className="text-xs text-gray-500">{party.company_name}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {showDropdown && searchResults.length === 0 && searchTerm.length > 2 && (
                <div className="absolute z-30 w-full mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-xl p-4 text-center">
                  <p className="text-sm text-gray-600">No {partyLabel.toLowerCase()} found</p>
                  <button className="mt-2 text-sm text-blue-600 hover:underline flex items-center justify-center gap-1 mx-auto">
                    <Plus className="w-3 h-3" />
                    Add New {partyLabel}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default DocumentPartyPicker;